/**
 * [ui1006, paczka KIT] REJESTR OBSZARÓW — „Wykaz KSC” (`/wykaz-ksc/…`, wejście z panelu `/admin/wykaz-ksc`).
 *
 * Jedno źródło nawigacji strony: 4 grupy, 16 obszarów (1 + 4 + 5 + 6) — rozdz. 3 projektu ui1006.
 * Wykaz jest WYJĄTKIEM ADRESOWYM: drzewem modułu zostaje `/wykaz-ksc/…` (EN `/ksc-registry/…`), bo te adresy stoją
 * w powiadomieniach zapisanych w bazie, działają na telefonie i czyta je test serwera. `/admin/wykaz-ksc` to wejście
 * z panelu na ten sam start, a `/admin/wykaz-ksc/<cokolwiek>` przekierowuje na `/wykaz-ksc/<to samo>` (router panelu).
 *
 * [paczka KSC 2026-10-06] `aktywna: true` — siedem starych stron zastąpiły obszary w `pages/wykazKsc/obszary/*` (treść na
 * `useRamaObszaru()`), źródła startu stoją w `pages/wykazKsc/zrodlaStartu.js` (3 odczyty), kreatory i obiekty z 3.4 projektu.
 */
import {
    FiInbox, FiSend, FiBookOpen, FiCalendar, FiBriefcase, FiGlobe, FiFileText, FiDatabase, FiBarChart2,
    FiPlayCircle, FiBell, FiUsers, FiMail, FiCheckSquare, FiClock, FiList,
} from "react-icons/fi";
import { GRUPA_PRACY, GRUPA_USTAWIEN, grupaStartu, grupaPracy, grupaUstawien, obszar, obszarStartu } from "./ksztalt";
import ZRODLA_STARTU_WYKAZU from "../../pages/wykazKsc/zrodlaStartu";
import { STARE_NAZWY_MIEJSC } from "../../pages/wykazKsc/slowaWykazu";

const GRUPA_REJESTROW = "rejestry";

/** Części karty spółki = wartości `?widok=` z dzisiejszych linków (bez zmian) + nowa `wpis`; brak parametru = `pola`. */
const CZESCI_KARTY_SPOLKI = ["wpis", "pola", "osoby", "zasoby", "umowy", "podpisujacy", "wnioski", "atestacja"];

const wykazKsc = {
    strona: "wykaz-ksc",
    tytul: "Wykaz KSC",
    zdanie: "Pilnuje, żeby każda zmiana danych spółki trafiła do wykazu.",
    baza: { pl: "/wykaz-ksc", en: "/ksc-registry" },
    wejsciePanelu: "/admin/wykaz-ksc",
    kluczStrony: "WykazKscPodglad",
    aktywna: true,

    grupy: [
        grupaStartu(),
        grupaPracy(),
        { id: GRUPA_REJESTROW, etykieta: "Rejestry" },
        grupaUstawien(),
    ],

    obszary: [
        obszarStartu({
            kluczTrasy: "wykazKsc",
            komponent: () => import("../../pages/wykazKsc/obszary/Start"),
            zdanie: "Pilnuje, żeby każda zmiana danych spółki trafiła do wykazu.",
            ikona: FiList,
            slowa: ["pulpit", "do załatwienia", "co wymaga uwagi", "start"],
        }),

        // ── Praca ──────────────────────────────────────────────────────────────────────────────
        obszar({
            id: "sprawy", grupa: GRUPA_PRACY, segment: { pl: "sprawy", en: "cases" }, kluczTrasy: "wykazKscSprawy",
            komponent: () => import("../../pages/wykazKsc/obszary/Sprawy"),
            etykieta: "Sprawy", zdanie: "Tu rozstrzygasz zmiany danych spółek do zgłoszenia ministrowi.",
            ikona: FiInbox, licznik: true,
            widoki: [
                { id: "otwarte", etykieta: "Otwarte" },
                { id: "po-terminie", etykieta: "Po terminie", licznik: true },
                { id: "wszystkie", etykieta: "Wszystkie" },
            ],
            filtry: ["spolka", "rodzaj", "stan"],
            obiekty: [{
                parametr: "sprawa", rodzaj: "karta", nazwa: "sprawa", dopelniacz: "sprawy",
                czesci: ["podsumowanie", "decyzja", "przypomnienia"],
            }],
            kreatory: [{ nazwa: "sprawa-reczna", tytul: "Dodaj sprawę ręczną", kroki: 3 }],
            pasek: { szukajka: "Szukaj sprawy…", wiecej: ["Dodaj sprawę ręczną…"] },
            slowa: ["sprawa", "zmiana danych", "termin", "14 dni", "decyzja", "po terminie"],
        }),
        obszar({
            id: "wnioski", grupa: GRUPA_PRACY, segment: { pl: "wnioski", en: "requests" }, kluczTrasy: "wykazKscWnioski",
            komponent: () => import("../../pages/wykazKsc/obszary/Wnioski"),
            etykieta: "Wnioski", zdanie: "Tu prowadzisz wniosek do ministra od przygotowania do zaświadczenia.",
            ikona: FiSend, licznik: true,
            widoki: [
                { id: "w-toku", etykieta: "W toku", licznik: true },
                { id: "zlozone", etykieta: "Złożone" },
                { id: "wszystkie", etykieta: "Wszystkie" },
            ],
            filtry: ["spolka"],
            obiekty: [{
                parametr: "wniosek", rodzaj: "karta", nazwa: "wniosek", dopelniacz: "wniosku",
                czesci: ["co-zrobic", "zaswiadczenie", "sprawy"],
            }],
            kreatory: [{ nazwa: "wniosek", tytul: "Przygotuj wniosek", kroki: 2 }],
            pasek: { akcjaGlowna: "Przygotuj wniosek" },
            slowa: ["wniosek", "minister", "zaświadczenie", "podpis", "portal", "wnioski spółki"],
        }),
        obszar({
            id: "prawo", grupa: GRUPA_PRACY, segment: { pl: "prawo", en: "law" }, kluczTrasy: "wykazKscPrawo",
            komponent: () => import("../../pages/wykazKsc/obszary/Prawo"),
            etykieta: "Zmiany prawa", zdanie: "Tu oceniasz, czy zmiana przepisu dotyczy naszych spółek.",
            ikona: FiBookOpen, licznik: true,
            widoki: [
                { id: "do-przegladu", etykieta: "Do przeglądu", licznik: true },
                { id: "wymaga-dzialan", etykieta: "Wymaga działań", licznik: true },
                { id: "bez-wplywu", etykieta: "Bez wpływu", licznik: true },
                { id: "wszystkie", etykieta: "Wszystkie" },
            ],
            obiekty: [{
                parametr: "zdarzenie", rodzaj: "karta", nazwa: "zmiana prawa", dopelniacz: "zmiany prawa",
                czesci: ["zmiana", "procedury", "przeglad"],
            }],
            slowa: ["prawo", "ustawa", "akt prawny", "zmiana przepisu", "przegląd wpływu"],
        }),
        obszar({
            id: "terminy", grupa: GRUPA_PRACY, segment: { pl: "terminy", en: "deadlines" }, kluczTrasy: "wykazKscTerminy",
            komponent: () => import("../../pages/wykazKsc/obszary/Terminy"),
            etykieta: "Terminy", zdanie: "Tu widzisz, co i kiedy trzeba zrobić z ustawy.",
            ikona: FiCalendar,
            widoki: [
                { id: "najblizsze", etykieta: "Najbliższe 30 dni" },
                { id: "po-terminie", etykieta: "Po terminie", licznik: true },
                { id: "wszystkie", etykieta: "Wszystkie" },
            ],
            filtry: ["spolka"],
            slowa: ["termin", "kalendarz ustawowy", "najbliższe terminy", "potwierdzenie zgodności"],
        }),

        // ── Rejestry ───────────────────────────────────────────────────────────────────────────
        obszar({
            id: "spolki", grupa: GRUPA_REJESTROW, segment: { pl: "spolki", en: "companies" }, kluczTrasy: "wykazKscSpolki",
            komponent: () => import("../../pages/wykazKsc/obszary/Spolki"),
            etykieta: "Spółki", zdanie: "Tu widzisz, które spółki pilnujemy i w jakim są stanie.",
            ikona: FiBriefcase,
            widoki: [
                { id: "w-wykazie", etykieta: "W wykazie" },
                { id: "bez-karty", etykieta: "Bez karty wpisu", licznik: true },
                { id: "poza-wykazem", etykieta: "Poza wykazem" },
            ],
            kreatory: [{ nazwa: "karta-wpisu", tytul: "Załóż kartę wpisu", kroki: 3 }],
            slowa: ["spółka", "karta spółki", "karta wpisu", "kafle spółek", "numer w wykazie"],
        }),
        obszar({
            id: "zasoby", grupa: GRUPA_REJESTROW, segment: { pl: "zasoby", en: "assets" }, kluczTrasy: "wykazKscZasoby",
            komponent: () => import("../../pages/wykazKsc/obszary/Zasoby"),
            etykieta: "Zasoby sieciowe", zdanie: "Tu prowadzisz domeny i adresy IP wszystkich spółek.",
            ikona: FiGlobe,
            filtry: ["spolka", "rodzaj"],
            obiekty: [{ parametr: "zasob", rodzaj: "formularz", nazwa: "zasób sieciowy", dopelniacz: "zasobu sieciowego" }],
            pasek: { szukajka: "Szukaj zasobu…", akcjaGlowna: "Dodaj zasób" },
            slowa: ["zasoby", "domena", "adres IP", "abonent"],
        }),
        obszar({
            id: "umowy", grupa: GRUPA_REJESTROW, segment: { pl: "umowy", en: "contracts" }, kluczTrasy: "wykazKscUmowy",
            komponent: () => import("../../pages/wykazKsc/obszary/Umowy"),
            etykieta: "Umowy z dostawcami", zdanie: "Tu prowadzisz dostawców IT i decyzje o dostawcy usług bezpieczeństwa.",
            ikona: FiFileText,
            filtry: ["spolka", "mssp"],
            obiekty: [{ parametr: "umowa", rodzaj: "formularz", nazwa: "umowa z dostawcą", dopelniacz: "umowy z dostawcą" }],
            pasek: { akcjaGlowna: "Dodaj umowę" },
            slowa: ["umowy", "dostawca", "dostawca usług bezpieczeństwa", "kontrahent"],
        }),
        obszar({
            id: "zrodla", grupa: GRUPA_REJESTROW, segment: { pl: "zrodla", en: "sources" }, kluczTrasy: "wykazKscZrodla",
            komponent: () => import("../../pages/wykazKsc/obszary/Zrodla"),
            etykieta: "Źródła danych", zdanie: "Tu sprawdzasz, czy system ma skąd brać dane.",
            ikona: FiDatabase, klucz: "WykazKscRedakcja", wzor: "S2 + S7", licznik: true,
            obiekty: [{ parametr: "zrodlo", rodzaj: "szuflada", nazwa: "źródło danych", dopelniacz: "źródła danych" }],
            slowa: ["źródła", "GUS", "CEIDG", "OVH", "KRS", "awaria źródła", "sprawdź teraz"],
        }),
        obszar({
            id: "raporty", grupa: GRUPA_REJESTROW, segment: { pl: "raporty", en: "reports" }, kluczTrasy: "wykazKscRaporty",
            komponent: () => import("../../pages/wykazKsc/obszary/Raporty"),
            etykieta: "Raporty", zdanie: "Tu wysyłasz zestawienie i pobierasz dowód nadzoru.",
            ikona: FiBarChart2, wzor: "dwie karty",
            filtry: ["miesiac"],
            pasek: { akcjaGlowna: "Pobierz raport (PDF)" },
            slowa: ["raport", "zestawienie tygodniowe", "raport miesięczny", "dowód nadzoru"],
        }),

        // ── Ustawienia ─────────────────────────────────────────────────────────────────────────
        obszar({
            id: "ustawienia-uruchomienie", grupa: GRUPA_USTAWIEN, segment: { pl: "uruchomienie", en: "setup" },
            kluczTrasy: "wykazKscUstUruchomienie",
            komponent: () => import("../../pages/wykazKsc/obszary/UstUruchomienie"),
            etykieta: "Uruchomienie", zdanie: "Tu widzisz, co zostało do zrobienia, żeby wykaz zaczął pilnować spółek.",
            ikona: FiPlayCircle, wzor: "S4",
            slowa: ["pierwsze uruchomienie", "kroki", "obserwacja", "hasła źródeł"],
        }),
        obszar({
            id: "ustawienia-tryb", grupa: GRUPA_USTAWIEN, segment: { pl: "tryb", en: "mode" },
            kluczTrasy: "wykazKscUstTryb",
            komponent: () => import("../../pages/wykazKsc/obszary/UstTryb"),
            etykieta: "Tryb powiadomień", zdanie: "Tu decydujesz, czy powiadomienia wychodzą do ludzi, czy system tylko liczy.",
            ikona: FiBell, wzor: "jedna decyzja",
            slowa: ["tryb próbny", "tryb pełny", "powiadomienia i przypomnienia", "włącz powiadomienia"],
        }),
        obszar({
            id: "ustawienia-powiadomienia", grupa: GRUPA_USTAWIEN, segment: { pl: "powiadomienia", en: "notifications" },
            kluczTrasy: "wykazKscUstPowiadomienia",
            komponent: () => import("../../pages/wykazKsc/obszary/UstPowiadomienia"),
            etykieta: "Kogo powiadamiamy", zdanie: "Tu ustawiasz, kto dostaje przypomnienia i zestawienia.",
            ikona: FiUsers, wzor: "S3",
            pasek: { akcjaGlowna: "Zapisz zmiany (2)" },
            slowa: ["adresaci", "przypomnienia", "zestawienie", "SMS do Zarządu", "Teams", "skrzynka nadawcza"],
        }),
        obszar({
            id: "ustawienia-skrzynka", grupa: GRUPA_USTAWIEN, segment: { pl: "skrzynka-urzedowa", en: "official-mailbox" },
            kluczTrasy: "wykazKscUstSkrzynka",
            komponent: () => import("../../pages/wykazKsc/obszary/UstSkrzynka"),
            etykieta: "Skrzynka urzędowa", zdanie: "Tu ustawiasz skrzynkę, na którą przychodzą pisma z urzędu.",
            ikona: FiMail, wzor: "S3",
            pasek: { akcjaGlowna: "Zapisz zmiany (2)" },
            slowa: ["ksc@", "zaufane domeny nadawców", "słowa do przejrzenia", "pisma urzędowe"],
        }),
        obszar({
            id: "ustawienia-spolki", grupa: GRUPA_USTAWIEN, segment: { pl: "spolki", en: "companies" },
            kluczTrasy: "wykazKscUstSpolki",
            komponent: () => import("../../pages/wykazKsc/obszary/UstSpolki"),
            etykieta: "Spółki objęte wykazem", zdanie: "Tu wskazujesz, które spółki są w wykazie, a które poza nim.",
            ikona: FiCheckSquare, wzor: "S3",
            pasek: { akcjaGlowna: "Zapisz zmiany (2)" },
            slowa: ["poza wykazem", "nie jest w wykazie", "sprawy wspólne", "sprzeczne z Procedurami"],
        }),
        obszar({
            id: "ustawienia-automaty", grupa: GRUPA_USTAWIEN, segment: { pl: "automaty", en: "schedules" },
            kluczTrasy: "wykazKscUstAutomaty",
            komponent: () => import("../../pages/wykazKsc/obszary/UstAutomaty"),
            etykieta: "Automaty i godziny", zdanie: "Tu ustawiasz godziny sprawdzeń i obserwowane akty prawne.",
            ikona: FiClock, wzor: "S3",
            pasek: { akcjaGlowna: "Zapisz zmiany (2)" },
            slowa: ["godziny", "harmonogram", "sprawozdanie w KRS", "obserwowane akty prawne"],
        }),
    ],

    /**
     * Karta spółki ma spółkę W ŚCIEŻCE (`/wykaz-ksc/spolka/:id`) — wyjątek zastany, bo adres stoi w linkach i w macierzy Procedur.
     * Trasa rysuje obszar „Spółki” z otwartą kartą; jej `?widok=` znaczy tu „część karty” (wartości bez zmian), a żeton
     * wewnątrz części to `&pokaz=`.
     */
    trasyObiektow: [{
        kluczTrasy: "wykazKscSpolka",
        segment: { pl: "spolka/:id", en: "company/:id" },
        obszar: "spolki",
        parametr: "id",
        parametrCzesci: "widok",
        rodzaj: "karta",
        nazwa: "karta spółki",
        dopelniacz: "karty spółki",
        czesci: CZESCI_KARTY_SPOLKI,
        czescDomyslna: "pola",
        pokaz: { pola: ["do-zgloszenia", "wszystkie"] },
        // Część „Potwierdzenie” to kreator OSADZONY w treści części (2 kroki + „Sprawdź”); jego krok stoi w `&krok=` karty.
        kreatorOsadzony: { czesc: "atestacja", kroki: 3 },
    }],

    /** Źródła listy „Do załatwienia” — trzy odczyty (`pages/wykazKsc/zrodlaStartu.js`), zgodne z planem niżej. */
    zrodlaStartu: ZRODLA_STARTU_WYKAZU,
    sufitOdczytowStartu: 3,
    zrodlaStartuPlan: [
        { id: "pulpit", odczyt: "GET /pulpit", klucz: "WykazKscPodglad" },
        { id: "sprawy", odczyt: "GET /sprawy?tylkoOtwarte", klucz: "WykazKscPodglad" },
        { id: "prawo", odczyt: "GET /prawo", klucz: "WykazKscPodglad" },
    ],

    /**
     * Stare adresy Wykazu: siedem dotychczasowych tras zostaje co do znaku (rysuje je trasa huba), `?sprawa=` i `?widok=`
     * karty spółki też. Jedyne przekierowanie — `/wykaz-ksc/ustawienia` na pierwszy obszar ustawień — robi wspólna reguła
     * `celStaregoAdresu` (adres samej grupy), więc ta funkcja nie ma własnych wierszy.
     */
    stareAdresy: () => null,
    stareSlugi: {},

    slownikPojec: [
        { slowo: "wykaz KSC", definicja: "Prowadzony przez ministra spis podmiotów krajowego systemu cyberbezpieczeństwa, do którego wpisane są nasze spółki." },
        { slowo: "sprawa", definicja: "Jedna wykryta zmiana danych spółki, o której trzeba zdecydować, czy zgłaszamy ją ministrowi." },
        { slowo: "termin ustawowy 14 dni", definicja: "Czas liczony od dnia zmiany, w którym ustawa każe zgłosić zmianę danych spółki do wykazu." },
        { slowo: "wniosek", definicja: "Pismo do ministra o wpis, zmianę wpisu albo wykreślenie, które obejmuje jedną lub kilka spraw spółki." },
        { slowo: "zaświadczenie", definicja: "Potwierdzenie z portalu ministra, że wniosek został złożony, a jego wgranie zamyka objęte nim sprawy." },
        { slowo: "stan zgłoszony", definicja: "Dane spółki w takiej postaci, w jakiej ostatnio zgłosiliśmy je ministrowi." },
        { slowo: "potwierdzenie zgodności", definicja: "Okresowe oświadczenie, że dane spółki w wykazie zgadzają się ze stanem faktycznym." },
        { slowo: "dostawca usług bezpieczeństwa", definicja: "Zewnętrzny wykonawca, który prowadzi dla spółki usługi cyberbezpieczeństwa i musi być wskazany we wpisie." },
        { slowo: "system S46", definicja: "Rządowy system wymiany informacji o cyberbezpieczeństwie, przez który składa się wnioski do wykazu." },
        { slowo: "tryb próbny", definicja: "System liczy terminy i pokazuje sprawy, ale nie wysyła niczego do ludzi." },
        { slowo: "tryb pełny", definicja: "System wysyła e-maile, przypomnienia do Zarządu i zestawienia tygodniowe." },
        { slowo: "źródło danych", definicja: "Rejestr albo usługa, z której system co dzień czyta dane spółek, żeby wykryć zmianę." },
        { slowo: "karta wpisu", definicja: "Zapis w aplikacji o tym, jak spółka jest wpisana do wykazu, od którego zaczyna się jej pilnowanie." },
    ],

    gdzieCoBylo: [
        { bylo: "Pulpit wykazu (osiem bloków)", jest: "Do załatwienia — lista rzeczy do zrobienia, stan i spółki", obszar: "start" },
        { bylo: "Sprawy", jest: "Praca → Sprawy", obszar: "sprawy" },
        { bylo: "Wnioski w karcie spółki", jest: "Praca → Wnioski", obszar: "wnioski" },
        { bylo: "Prawo", jest: "Praca → Zmiany prawa", obszar: "prawo" },
        { bylo: "Najbliższe terminy na pulpicie", jest: "Praca → Terminy", obszar: "terminy" },
        { bylo: "Kafle spółek na pulpicie", jest: "Rejestry → Spółki", obszar: "spolki" },
        { bylo: "Zasoby", jest: "Rejestry → Zasoby sieciowe", obszar: "zasoby" },
        { bylo: "Umowy", jest: "Rejestry → Umowy z dostawcami", obszar: "umowy" },
        { bylo: "Źródła", jest: "Rejestry → Źródła danych", obszar: "zrodla" },
        { bylo: "Pierwsze uruchomienie (karta na pulpicie)", jest: "Ustawienia → Uruchomienie", obszar: "ustawienia-uruchomienie" },
        { bylo: "Ustawienia: Powiadomienia i przypomnienia", jest: "Ustawienia → Tryb powiadomień", obszar: "ustawienia-tryb" },
        { bylo: "Ustawienia: adresaci i przypomnienia", jest: "Ustawienia → Kogo powiadamiamy", obszar: "ustawienia-powiadomienia" },
        { bylo: "Ustawienia: skrzynka i zaufane domeny", jest: "Ustawienia → Skrzynka urzędowa", obszar: "ustawienia-skrzynka" },
        { bylo: "„Nie jest w wykazie” w pierwszym uruchomieniu", jest: "Ustawienia → Spółki objęte wykazem", obszar: "ustawienia-spolki" },
        { bylo: "Ustawienia: godziny i akty prawne", jest: "Ustawienia → Automaty i godziny", obszar: "ustawienia-automaty" },
    ],

    /** Stare nazwy miejsc w tekstach serwera (e-maile, powiadomienia) → nowe nazwy; serwera nie zmieniamy. Jedno źródło z `tekstSerwera`
        (`pages/wykazKsc/slowaWykazu.js`), które przepuszcza przez nie każdy tekst serwera na ekranie (F133). */
    stareNazwyMiejsc: STARE_NAZWY_MIEJSC,
};

export default wykazKsc;
